import {Loader} from "@googlemaps/js-api-loader";
import LOADER_OPTIONS from "../utils/maps-config.js";
import {MarkerClusterer} from "@googlemaps/markerclusterer";
import EventsFieldPeriod from "./events-field-period";
import Pagination from "./pagination";
import Spinner from "./spinner";
import {getEventInfoWindowContent, getEventsInfoWindowContent} from "../utils/event-info-window.js";
import Swiper from 'swiper';
import { Navigation } from "swiper/modules";

class EventsCalendar {
    constructor() {
        this.container = document.getElementById("events-calendar");

        if (!this.container) {
            return;
        }

        this.searchForm = this.container.querySelector(".js-events-calendar-form");
        this.list = this.container.querySelector(".js-events-calendar-list");
        this.spinner = new Spinner(this.container.querySelector(".js-events-calendar-loading"));
        this.map = this.container.querySelector(".js-events-calendar-map");

        // Parametri per la ricerca.
        this.searchParams = new URLSearchParams(document.location.search);
        this.searchParams.delete("pag");

        // Elenco degli eventi.
        this.events = [];

        // Numero di elementi da mostrare per ogni pagina.
        this.items_per_page = 6;

        // Istanza alle API di Google Maps.
        this.gApi = null;
        this.gMap = null;
        this.defaultCenterMap = {lat: 41.8906348, lng: 12.4921323}; // Colosseo
        this.defaultZoomMap = 7;

        this.#updateSearchForm();
        this.#bindEvents();
        this.#preparePeriodFilter();
        this.#fetchEvents();
    }

    /**
     * Aggiorna i campi del modulo di ricerca usando i dati della URLSearchParams.
     */
    #updateSearchForm() {
        this.searchForm.querySelectorAll("input, select").forEach(field => {
            field.value = this.searchParams.get(field.name) || "";
        });
    }

    /**
     * Aggancia gli eventi.
     */
    #bindEvents() {
        // Invio del modulo.
        this.searchForm.addEventListener("submit", this.#onFormSubmit.bind(this));

        // Paginazione.
        this.list.addEventListener("click", this.#onPaginationClick.bind(this));

        // Questo serve a gestire lo storico del browser (per andare avanti e indietro con le frecce).
        window.addEventListener("popstate", this.#historyPop.bind(this));
    }

    #preparePeriodFilter() {
        new EventsFieldPeriod(this.searchForm.period);
    }

    /**
     * Gestisce l’invio del modulo di ricerca.
     */
    #onFormSubmit(event) {
        event.preventDefault();

        // Genera un URL usando i dati della form
        let url = new URL(this.searchForm.action);
        (new FormData(this.searchForm)).forEach((value, name) => {
            if ("" === value) {
                return;
            }

            url.searchParams.append(name, value);
        });

        // Esegue la ricerca se i filtri sono stati cambiati
        if (url.searchParams.toString() !== this.searchParams.toString()) {
            this.searchParams = url.searchParams;

            this.#historyPush(url);
            this.#fetchEvents();
        }
    }

    /**
     * Gestisce il click sui link della paginazione.
     */
    #onPaginationClick(event) {
        // delegation pattern
        let target = event.target.closest(".page-numbers");

        if (!target) {
            return;
        }

        event.preventDefault();

        let url = new URL(target.getAttribute("href"));
        this.searchParams = url.searchParams;

        this.#historyPush(url);
        this.#renderList();
    }

    /**
     * Aggiorna l’url della pagina.
     */
    #historyPush(url) {
        let state = {
            name: "events-calendar",
            url: url.toString(),
        };

        window.history.pushState(state, "", url);
    }

    /**
     * Gestisce lo storico del browser.
     */
    #historyPop(event) {
        if (event?.state?.name === "events-calendar" && event?.state?.url) {
            this.searchParams = new URL(event.state.url).searchParams;

            this.#updateSearchForm();
            this.#fetchEvents();
        }
    }

    /**
     * Esegue una chiamata ajax per ottenere gli eventi e mostrarli.
     */
    #fetchEvents() {
        // Start loading
        this.spinner.start();
        this.list.innerHTML = "";

        const url = new URL(this.searchForm.dataset.ajaxUrl);

        // Riporto i campi della form nella url della rest api da usare
        this.searchParams.forEach((value, key) => {
            if ("location" === key) {
                key = "city";
            }

            url.searchParams.set(key, value);
        });

        // Evito la paginazione dei risultati
        url.searchParams.set("nopaging", "1");

        fetch(url.toString())
            .then(response => response.json())
            .then(data => {
                this.events = data.events;
                this.#renderList();
                this.#renderMap();

                // End loading
                this.spinner.end();
            });
    }

    /**
     * Renderizza il blocco degli eventi.
     */
    #renderList() {
        this.list.innerHTML = this.#getListHtml();
    }

    /**
     * Restituisce il codice HTML per la tab con l’elenco degli eventi.
     */
    #getListHtml() {
        if (0 === this.events.length) {
            return `<div class="c-alert -info">Non ci sono eventi con i filtri impostati.</div>`;
        }

        let current_page = parseInt((new URL(document.URL)).searchParams.get("pag") || 1);

        return this.#getEventsHtml(current_page) + this.#getEventsPaginationHtml(current_page);
    }

    /**
     * Restituisce il codice HTML per l’elenco degli eventi.
     */
    #getEventsHtml(current_page) {
        let start = (current_page - 1) * this.items_per_page;
        let end = start + this.items_per_page;

        if (end > this.events.length) {
            end = this.events.length;
        }

        let listHtml = this.events
            .slice(start, end)
            .map(this.#getEventHtml)
            .join("");

        return `<div class="o-grid:three u-row u-row@chess-three u-align:stretch">${listHtml}</div>`;
    }

    /**
     * Genera il codice HTML per il singolo evento.
     */
    #getEventHtml(event) {
        let placeHtml = '';
        let cityHtml = '';

        if (event.place) {
            placeHtml = `<ul class="c-card_cat c-list -meta"><li><a href="${event.place.url}">${event.place.title}</a></li></ul>`;

            if (event.place.city) {
                cityHtml = `<a href="${event.place.city.url}">${event.place.city.title}</a>`;
            }
        }

        return `
<div class="c-card :event :standard">
    <figure class="c-card_media">
        <a href="${event.url}">${event.image}</a>
    </figure>
    <div class="c-card_body u-cq">
        <div class="c-card_head">
            ${placeHtml}
            <h3 class="c-card_title"><a href="${event.url}">${event.title}</a></h3>
        </div>
    </div>
    <div class="c-card_foot">
        <ul class="c-list">
            <li>
                <svg role="img" focusable="false">
                    <use xlink:href="${m6e.spriteUrl}#icon--place"></use>
                </svg>
                ${cityHtml}
            </li>
            <li>
                <svg role="img" focusable="false">
                    <use xlink:href="${m6e.spriteUrl}#icon--calendar"></use>
                </svg>
                ${event.dates}
            </li>
        </ul>
    </div>
</div>`;
    }

    /**
     * Restituisce il codice HTML per la paginazione degli eventi.
     */
    #getEventsPaginationHtml(current_page) {
        let pagination = (new Pagination()).render(
            document.URL,
            this.events.length,
            this.items_per_page,
            current_page,
            {
                screen_reader_text: "Navigazione eventi",
                aria_label: "Paginazione eventi"
            }
        );

        return '<div class="t-archive_pagination js-events-calendar-pagination">' + pagination + '</div>';
    }

    /**
     * Renderizza la mappa al primo caricamento della pagina.
     *
     * @todo La mappa andrebbe renderizzata solo quando si apre la tab corrispettiva.
     */
    #renderMap() {
        (new Loader(LOADER_OPTIONS))
            .load()
            .then(google => {
                this.gApi = google;
                this.gMap = new this.gApi.maps.Map(this.map, {
                    zoom: this.defaultZoomMap,
                    center: this.defaultCenterMap,
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: true,
                });

                this.#updateMap();
            });
    }

    /**
     * Aggiorna i marcatori nella mappa.
     */
    #updateMap() {
        if (0 === this.events.length) {
            new MarkerClusterer({
                map: this.gMap,
                markers: []
            });

            this.gMap.setCenter(this.defaultCenterMap);
            this.gMap.setZoom(this.defaultZoomMap);

            return;
        }

        const svg = window.btoa(
            `<svg fill="#ffffff" stroke="#aaaaaa" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                <filter id='inset-shadow'>
                    <feOffset dx='0' dy='-5' />
                    <feGaussianBlur stdDeviation='2' result='offset-blur' />
                    <feComposite operator='out' in='SourceGraphic' in2='offset-blur' result='inverse' />
                    <feFlood flood-color='black' flood-opacity='.35' result='color' />
                    <feComposite operator='in' in='color' in2='inverse' result='shadow' />
                    <feComposite operator='over' in='shadow' in2='SourceGraphic' /> 
                </filter>
                <g filter="url(#inset-shadow)">
                    <circle cx="50" cy="50" r="15" />
                </g>
            </svg>`
        );

        const infoWindow = new this.gApi.maps.InfoWindow({
            content: "",
            disableAutoPan: false,
            maxWidth: 280,
        });

        /**
         * Attiva Swiper nelle finestre dei luoghi che hanno più eventi.
         * @link https://developers.google.com/maps/documentation/javascript/reference/info-window?hl=it#InfoWindow.visible
         */
        infoWindow.addListener("visible", () => {
            new Swiper(".js-infowindow-swiper", {
                modules: [Navigation],
                navigation: {
                    nextEl: ".js-infowindow-swiper-next",
                    prevEl: ".js-infowindow-swiper-prev",
                }
            });
        });

        const markersBounds = new this.gApi.maps.LatLngBounds();

        /**
         * Attenzione! Questa non è una mappa di Google, ma una classe nativa di JavaScript!
         * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map/has
         */
        const places = new Map();
        this.events.forEach(event => {
            let placeId = event.place?.id;
            let placeMap = event.place?.map;

            if (!placeId || !placeMap) {
                return;
            }

            if (!places.has(placeId)) {
                places.set(placeId, {
                    position: {
                        lat: placeMap.lat,
                        lng: placeMap.lng,
                    },
                    events: []
                });
            }

            places.get(placeId).events.push(event);
        });

        const markers = [];
        places.forEach((place) => {
            const marker = new this.gApi.maps.Marker({
                position: place.position,
                map: this.gMap,
            });

            markersBounds.extend(marker.position);

            marker.addListener("click", () => {
                if (place.events.length === 1) {
                    infoWindow.setContent(
                        getEventInfoWindowContent(place.events[0])
                    );
                } else {
                    infoWindow.setContent(
                        getEventsInfoWindowContent(place.events)
                    );
                }

                infoWindow.open({
                    anchor: marker,
                    map: this.gMap,
                });
            });

            markers.push(marker);
        });

        new MarkerClusterer({
            map: this.gMap,
            markers,
            renderer: {
                render: ({count, position}) => new this.gApi.maps.Marker({
                    position,
                    label: {
                        text: String(count),
                        color: "var(--color-accent)",
                        fontWeight: "600",
                        fontSize: "12px",
                    },
                    icon: {
                        url: `data:image/svg+xml;base64,${svg}`,
                        scaledSize: new this.gApi.maps.Size(100, 100),
                        anchor: new this.gApi.maps.Point(50, 50)
                    },
                    zIndex: Number(this.gApi.maps.Marker.MAX_ZINDEX) + count, // adjust zIndex to be above other markers
                }),
            },
        });

        /**
         * Commentare la prima riga e attivare le due successive per centrare la mappa sull’Arsenale di Venezia,
         * caso particolare che contiene molti eventi al suo interno.
         *
         * @link https://www.artribune.test/calendario-eventi/?location=venezia&period=2022-05-01%7E2022-05-31
         */
        this.gMap.fitBounds(markersBounds);
        // this.gMap.setCenter({lat: 45.433215, lng: 12.351465}); // Arsenale
        // this.gMap.setZoom(18); // Arsenale
    }
}

export default EventsCalendar;
